import React, { useState } from 'react';
import apiClient from '../../../api/client';

// =====================================================================
// Types
// =====================================================================

interface Registration {
  parent_code: string;
  version_code: string;
  lot_code: string;
}

interface RegistrationSectionProps {
  batchCode: string;
  status: string;
  inchiKey: string | null;
  registration: Registration | null;
  onRegistered: () => void; // callback to refresh detail
}

interface RegisterResponse {
  batch_code: string;
  status: string;
  registered_parent_code: string;
  registered_version_code: string;
  registered_lot_code: string;
  parent_existed: boolean;
  related_batches: { batch_code: string; status: string }[];
}

// =====================================================================
// Component
// =====================================================================

const RegistrationSection: React.FC<RegistrationSectionProps> = ({
  batchCode, status, inchiKey, registration, onRegistered,
}) => {
  const [showConfirm, setShowConfirm] = useState(false);
  const [registering, setRegistering] = useState(false);
  const [result, setResult] = useState<RegisterResponse | null>(null);
  const [error, setError] = useState<string | null>(null);

  const handleRegister = async () => {
    setRegistering(true);
    setError(null);
    try {
      const resp = await apiClient.post(`/batches/${batchCode}/register`, {});
      setResult(resp.data);
      setShowConfirm(false);
      // refresh parent after short delay so user sees the success message
      setTimeout(() => onRegistered(), 1500);
    } catch (err: any) {
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail;
      if (status === 409) {
        setError('This batch is already registered');
      } else {
        setError(detail || 'Registration failed');
      }
      setShowConfirm(false);
    } finally {
      setRegistering(false);
    }
  };

  // ─── Already registered: show info card ───
  if (registration) {
    return (
      <div style={{
        backgroundColor: '#f0fdfa', borderLeft: '4px solid #0d9488',
        borderRadius: 6, padding: '10px 12px', marginBottom: 8,
      }}>
        <div style={{ fontSize: 10, color: '#0d9488', textTransform: 'uppercase', letterSpacing: 0.5, fontWeight: 600, marginBottom: 6 }}>
          Registered Compound
        </div>
        <div style={{ fontSize: 16, fontFamily: 'monospace', fontWeight: 700, color: '#0f766e', marginBottom: 4 }}>
          {registration.parent_code}
        </div>
        <div style={{ display: 'flex', gap: 16, fontSize: 12, fontFamily: 'monospace', color: '#6b7280' }}>
          <span>Version: {registration.version_code}</span>
          <span>Lot: {registration.lot_code}</span>
        </div>
        <div style={{ marginTop: 6 }}>
          <span style={{
            display: 'inline-flex', alignItems: 'center', padding: '2px 8px', borderRadius: 4,
            fontSize: 11, fontWeight: 500, backgroundColor: '#d1fae5', color: '#065f46', border: '1px solid #6ee7b7',
          }}>
            Registered
          </span>
        </div>
      </div>
    );
  }

  // ─── Not eligible for registration ───
  if (status === 'registered' || status === 'retired') return null;

  const canRegister = !!inchiKey;

  // ─── Success result (before detail refreshes) ───
  if (result) {
    return (
      <div onClick={e => e.stopPropagation()} style={{
        backgroundColor: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 6,
        padding: 10, marginBottom: 8,
      }}>
        <div style={{ fontSize: 13, fontWeight: 600, color: '#166534', marginBottom: 4 }}>
          Registered as {result.registered_parent_code}
        </div>
        <div style={{ fontSize: 12, fontFamily: 'monospace', color: '#6b7280', marginBottom: 4 }}>
          {result.registered_version_code} / {result.registered_lot_code}
        </div>
        {result.parent_existed && (
          <div style={{ fontSize: 12, color: '#92400e', backgroundColor: '#fffbeb', border: '1px solid #fcd34d', borderRadius: 4, padding: '4px 8px', marginBottom: 4 }}>
            Linked to existing compound {result.registered_parent_code}
          </div>
        )}
        {result.related_batches && result.related_batches.length > 0 && (
          <div style={{ fontSize: 11, color: '#6b7280' }}>
            Related batches: {result.related_batches.map(rb => rb.batch_code).join(', ')}
          </div>
        )}
      </div>
    );
  }

  // ─── Error message ───
  if (error) {
    const isWarning = error.includes('already registered');
    return (
      <div onClick={e => e.stopPropagation()} style={{ marginBottom: 8 }}>
        <div style={{
          fontSize: 12, padding: '6px 10px', borderRadius: 4,
          backgroundColor: isWarning ? '#fffbeb' : '#fef2f2',
          color: isWarning ? '#92400e' : '#991b1b',
          border: `1px solid ${isWarning ? '#fcd34d' : '#fecaca'}`,
          marginBottom: 4,
        }}>
          {error}
        </div>
        <button onClick={e => { e.stopPropagation(); setError(null); }}
          style={{ fontSize: 11, color: '#6b7280', background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}>
          Dismiss
        </button>
      </div>
    );
  }

  // ─── Confirmation panel ───
  if (showConfirm) {
    return (
      <div onClick={e => e.stopPropagation()} style={{
        backgroundColor: '#f0fdfa', border: '1px solid #99f6e4', borderRadius: 6,
        padding: 10, marginBottom: 8,
      }}>
        <div style={{ fontSize: 13, fontWeight: 600, color: '#111827', marginBottom: 4 }}>
          Register {batchCode} as a compound?
        </div>
        <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 10 }}>
          This will assign a DIS registration number and link this batch to the compound registry.
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button onClick={handleRegister} disabled={registering}
            style={{
              padding: '6px 16px', fontSize: 12, borderRadius: 6, border: 'none',
              backgroundColor: '#0d9488', color: '#ffffff', cursor: registering ? 'wait' : 'pointer',
              opacity: registering ? 0.7 : 1,
            }}>
            {registering ? 'Registering...' : 'Register'}
          </button>
          <button onClick={() => setShowConfirm(false)} disabled={registering}
            style={{ padding: '6px 12px', fontSize: 12, borderRadius: 6, border: 'none', backgroundColor: 'transparent', color: '#6b7280', cursor: 'pointer' }}>
            Cancel
          </button>
        </div>
      </div>
    );
  }

  // ─── Register button ───
  return (
    <div onClick={e => e.stopPropagation()} style={{ marginBottom: 8 }}>
      <button
        onClick={() => canRegister && setShowConfirm(true)}
        disabled={!canRegister}
        title={!canRegister ? 'Cannot register \u2014 no InChI key available' : `Register ${batchCode}`}
        style={{
          display: 'inline-flex', alignItems: 'center', gap: 5,
          padding: '5px 12px', fontSize: 11, borderRadius: 4,
          border: '1px solid #99f6e4', backgroundColor: 'transparent', color: '#0d9488',
          cursor: canRegister ? 'pointer' : 'not-allowed',
          opacity: canRegister ? 1 : 0.5,
        }}
      >
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width={13} height={13}>
          <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
          <polyline points="22 4 12 14.01 9 11.01" />
        </svg>
        Register Compound
      </button>
    </div>
  );
};

export default RegistrationSection;
